// src/store/canvasObjectSlice.ts
import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_PRINT_TEMPLATE, getTemplateSide, getTemplateSides } from "../config/printTemplates";

const defaultTemplateSide = getTemplateSide(DEFAULT_PRINT_TEMPLATE, "front");

const initialState: any = {
  currentSide: "front",
  frontCanvasJSON: null,
  backCanvasJSON: null,
  shapeId: null,
  shapeInfo: {},
  previewEnabled: false,
  deleteRequested: false,
  duplicateRequested: false,
  selectedItem: null,
  selectedItemType: null,
  canvas: null,
  printArea: null,
  printAreaWidth: defaultTemplateSide.logicalWidth,
  printAreaHeight: defaultTemplateSide.logicalHeight,
  printAreaScale: 1,
  designTarget: {
    productId: null,
    productType: DEFAULT_PRINT_TEMPLATE.productType,
    printTemplateId: DEFAULT_PRINT_TEMPLATE.id,
    availableSides: getTemplateSides(DEFAULT_PRINT_TEMPLATE),
    selectedVariantId: null,
    selectedColor: "#ffffff",
    selectedSize: null,
    quantity: 1,
    designDraftId: null as string | null,
  },
  isGridEnabled: false,
  history: [],
  future: [],
  historyIndex: -1,
  // 🆕 New feature states
  show3DMockup: false,
  mockupSettings: {
    color: '#ffffff',
    material: 'cotton',
  },
  // 🎯 Advanced Canvas Guides & Snapping
  canvasGuides: {
    grid: {
      enabled: false,
      size: 20,
      majorLineInterval: 5,
      minorLineColor: "#E0E0E0",
      majorLineColor: "#BDBDBD",
      minorLineWidth: 0.5,
      majorLineWidth: 1,
      opacity: 0.6,
    },
    safeArea: {
      enabled: true, // Keep the lines
      margin: 20, // Safe zone - 20px from canvas edge (keep text/logos here)
      color: "#4CAF50",
      lineWidth: 1.5,
      dashArray: [8, 4],
      fillOpacity: 0, // REMOVED background rectangle (was 0.05)
      smartGuides: true,
      proximityDistance: 50,
      showLabel: true,
    },
    bleeding: {
      enabled: true,
      margin: 0, // Bleed zone - at canvas edge (0px - the actual canvas border)
      color: "#F44336",
      lineWidth: 2,
      dashArray: [8, 4],
      fillOpacity: 0,
      smartGuides: true,
      proximityDistance: 30,
      showLabel: false,
    },
    snapping: {
      enabled: true,
      snapToGrid: true,
      snapToObjects: true,
      snapToGuides: true,
      snapDistance: 8,
    },
    forceVisible: false, // Toggle for "Show Print Guides"
  },
  // 📐 Ruler Guides System
  rulerGuides: {
    enabled: true,
    visible: true,
    snapToGuides: true,
    locked: false,
    color: "#00A8FF",
    hoverColor: "#0080FF",
    snapDistance: 8,
  },
};

const canvasObjectSlice = createSlice({
  name: "canvasObject",
  initialState,
  reducers: {
    setSelectedObjectId: (state, action) => {
      state.shapeId = action.payload;
    },
    updateObjectProps: (state, action) => {
      const { id, props } = action.payload;
      if (state.shapeId === id) {
        state.selectedItemType = action.payload?.props?.type || null;
        state.shapeInfo = {
          ...state.shapeInfo,
          ...props,
        };
      }
    },
    togglePreview: (state) => {
      state.previewEnabled = !state.previewEnabled;
    },
    triggerDelete(state) {
      state.deleteRequested = true;
    },
    triggerDuplicate(state) {
      state.duplicateRequested = true;
    },
    clearCommands(state) {
      state.deleteRequested = false;
      state.duplicateRequested = false;
    },
    selectedItemAction(state, action) {
      const payload = action.payload;
      if (typeof payload === "string") {
        state.selectedItem = payload;
        state.selectedItemType = null;
      } else if (payload && typeof payload === "object") {
        state.selectedItem = payload.id ?? null;
        state.selectedItemType = payload.type ?? null;
      } else {
        state.selectedItem = null;
        state.selectedItemType = null;
      }
    },
    setCanvas(state, action) {
      state.canvas = action.payload;
    },
    setPrintArea(state, action) {
      state.printArea = action.payload;
    },
    setDesignTarget(state, action) {
      const nextTarget = {
        ...state.designTarget,
        ...action.payload,
      };
      state.designTarget = nextTarget;
      if (Array.isArray(nextTarget.availableSides) && !nextTarget.availableSides.includes(state.currentSide)) {
        state.currentSide = nextTarget.availableSides[0] || "front";
      }
      if (typeof action.payload?.printAreaWidth === "number") {
        state.printAreaWidth = action.payload.printAreaWidth;
      }
      if (typeof action.payload?.printAreaHeight === "number") {
        state.printAreaHeight = action.payload.printAreaHeight;
      }
    },
    setGridEnabled(state, action) {
      state.isGridEnabled = action.payload;
    },
    pushHistory(state, action) {
      state.history.push(action.payload);
      state.future = []; // clear redo stack on new change
    },
    undo(state) {
      if (state.history.length > 1) {
        const last = state.history.pop();
        state.future.push(last);
      }
    },
    redo(state) {
      if (state.future.length > 0) {
        const restored = state.future.pop();
        state.history.push(restored);
      }
    },

    setCurrentSide(state, action) {
      state.currentSide = action.payload;
    },
    saveCanvasJSON(state, action) {
      const { side, json } = action.payload;
      if (side === "front") state.frontCanvasJSON = json;
      if (side === "back") state.backCanvasJSON = json;
    },
    setFrontCanvasJSON(state, action) {
      state.frontCanvasJSON = action.payload;
    },
    setBackCanvasJSON(state, action) {
      state.backCanvasJSON = action.payload;
    },
    resetCanvasState: () => initialState,
    
    // 🆕 New feature actions
    toggle3DMockup(state) {
      state.show3DMockup = !state.show3DMockup;
    },
    setMockupSettings(state, action) {
      state.mockupSettings = { ...state.mockupSettings, ...action.payload };
    },
    
    // 🎯 Canvas Guides & Snapping Actions
    toggleGrid(state) {
      state.canvasGuides.grid.enabled = !state.canvasGuides.grid.enabled;
    },
    setGridSize(state, action) {
      state.canvasGuides.grid.size = action.payload;
    },
    toggleSafeArea(state) {
      state.canvasGuides.safeArea.enabled = !state.canvasGuides.safeArea.enabled;
    },
    setSafeAreaMargin(state, action) {
      state.canvasGuides.safeArea.margin = action.payload;
    },
    toggleSafeAreaSmartGuides(state) {
      state.canvasGuides.safeArea.smartGuides = !state.canvasGuides.safeArea.smartGuides;
    },
    toggleBleeding(state) {
      state.canvasGuides.bleeding.enabled = !state.canvasGuides.bleeding.enabled;
    },
    setBleedingMargin(state, action) {
      state.canvasGuides.bleeding.margin = action.payload;
    },
    toggleBleedingSmartGuides(state) {
      state.canvasGuides.bleeding.smartGuides = !state.canvasGuides.bleeding.smartGuides;
    },
    toggleSnapping(state) {
      state.canvasGuides.snapping.enabled = !state.canvasGuides.snapping.enabled;
    },
    toggleSnapToGrid(state) {
      state.canvasGuides.snapping.snapToGrid = !state.canvasGuides.snapping.snapToGrid;
    },
    toggleSnapToObjects(state) {
      state.canvasGuides.snapping.snapToObjects = !state.canvasGuides.snapping.snapToObjects;
    },
    toggleSnapToGuides(state) {
      state.canvasGuides.snapping.snapToGuides = !state.canvasGuides.snapping.snapToGuides;
    },
    setSnapDistance(state, action) {
      state.canvasGuides.snapping.snapDistance = action.payload;
    },
    updateCanvasGuidesConfig(state, action) {
      state.canvasGuides = { ...state.canvasGuides, ...action.payload };
    },
    toggleForceVisible(state) {
      state.canvasGuides.forceVisible = !state.canvasGuides.forceVisible;
    },
    toggleLabels(state) {
      const newValue = !state.canvasGuides.safeArea.showLabel;
      state.canvasGuides.safeArea.showLabel = newValue;
      state.canvasGuides.bleeding.showLabel = newValue;
    },
    
    // 📐 Ruler Guides Actions
    toggleRulerGuides(state) {
      state.rulerGuides.enabled = !state.rulerGuides.enabled;
    },
    toggleRulerVisibility(state) {
      state.rulerGuides.visible = !state.rulerGuides.visible;
    },
    toggleRulerSnap(state) {
      state.rulerGuides.snapToGuides = !state.rulerGuides.snapToGuides;
    },
    toggleRulerLock(state) {
      state.rulerGuides.locked = !state.rulerGuides.locked;
    },
    setRulerSnapDistance(state, action) {
      state.rulerGuides.snapDistance = action.payload;
    },
  },
});

export const {
  setSelectedObjectId,
  updateObjectProps,
  togglePreview,
  triggerDelete,
  triggerDuplicate,
  clearCommands,
  selectedItemAction,
  setCanvas,
  setPrintArea,
  setDesignTarget,
  setGridEnabled,

  pushHistory,
  undo,
  redo,

  setCurrentSide,
  saveCanvasJSON,
  setFrontCanvasJSON,
  setBackCanvasJSON,
  resetCanvasState,
  
  // 🆕 New feature actions
  toggle3DMockup,
  setMockupSettings,
  
  // 🎯 Canvas Guides & Snapping Actions
  toggleGrid,
  setGridSize,
  toggleSafeArea,
  setSafeAreaMargin,
  toggleSafeAreaSmartGuides,
  toggleBleeding,
  setBleedingMargin,
  toggleBleedingSmartGuides,
  toggleSnapping,
  toggleSnapToGrid,
  toggleSnapToObjects,
  toggleSnapToGuides,
  setSnapDistance,
  updateCanvasGuidesConfig,
  toggleForceVisible,
  toggleLabels,
  
  // 📐 Ruler Guides Actions
  toggleRulerGuides,
  toggleRulerVisibility,
  toggleRulerSnap,
  toggleRulerLock,
  setRulerSnapDistance,
} = canvasObjectSlice.actions;
export default canvasObjectSlice.reducer;
